import { useEffect, useMemo, useState } from "react";
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react";
import { Address, toNano } from "@ton/core";
import {
  buildBroadcastPayload,
  buildWithdrawArgs,
  buildWithdrawMessageBody,
  isValidNote,
} from "@tonado/core";
import { getPool, assetIdLabel } from "../lib/pools";
import { Steps } from "./Steps";
import { parseNoteMetadata, type Note } from "../lib/note";
import { loadStoredNotes } from "../lib/storage";
import { loadCommitmentsFromRelayer } from "@tonado/core";
import { RELAYER_URL } from "../lib/env";
import {
  discoverRelayers,
  resolveRelayerAddress,
  type RelayerEndpoint,
} from "../lib/relayerRegistry";
import { broadcastWithdraw, getStatus } from "../lib/relayer";
import { poolIsSpent } from "../lib/relayerRpc";
import { friendlyError, type FriendlyError } from "../lib/friendlyError";

type Stage = "input" | "ready" | "fetching-tree" | "proving" | "send" | "done";

// Attach 0.3 TON for gas; pool refunds attached − REFUND_RESERVE.
const WITHDRAW_ATTACHED = toNano("0.3");

/** Sentinel for "self-relay" — user's connected wallet pays gas + signs. */
const SELF_RELAY = "self" as const;
type RelayerChoice = typeof SELF_RELAY | RelayerEndpoint;

/**
 * Compute the fee bound into the SNARK from the relayer's /status
 * response. Matches `fee = max(denomination * feeBps / 10000, feeMinNanoTon)`.
 * The relayer enforces an identical formula before broadcasting; the
 * pool enforces `fee <= denomination`. Both verifications use this
 * exact value.
 */
function computeRelayerFee(denomination: bigint, feeBps: number, feeMinNanoTon: bigint): bigint {
  const computed = (denomination * BigInt(feeBps)) / 10_000n;
  return computed > feeMinNanoTon ? computed : feeMinNanoTon;
}

function endpointHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function Withdraw() {
  const [tonConnectUI] = useTonConnectUI();
  const sender = useTonAddress();
  const connected = Boolean(sender);

  const [raw, setRaw] = useState("");
  const [recipient, setRecipient] = useState("");
  const [stage, setStage] = useState<Stage>("input");
  const [error, setError] = useState<FriendlyError | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  // Sync metadata extract for UI feedback. Real Poseidon-aware parse
  // happens at withdraw time inside `buildWithdrawArgs`.
  const note: Note | null = useMemo(() => (raw.trim() ? parseNoteMetadata(raw) : null), [raw]);
  const parseErr =
    raw.trim() && !note
      ? "Invalid note. Expected: tonado-<asset>-<amount>-<network>-0x<124 hex>"
      : null;

  const pool = note ? getPool(`${note.asset}-${note.amount}`) : undefined;
  const recipientValid = isPlausibleTonAddress(recipient);

  // Auto-fill from saved notes — show a "use saved" picker if any exist.
  const [showSaved, setShowSaved] = useState(false);
  const savedNotes = useMemo(() => loadStoredNotes(), [showSaved]);

  // Relayer picker. Defaults to self-relay (the legacy behavior); when
  // the dApp is configured with VITE_RELAYER_REGISTRY_ADDRESS, the
  // dropdown surfaces every active bonded relayer too.
  // Default to the configured network relayer; fall back to self-relay
  // only if the user explicitly picks it. The actual RelayerEndpoint
  // object is set inside the discover effect below — until that
  // resolves we hold SELF_RELAY as a safe placeholder so the picker
  // doesn't show a half-initialised state.
  const [relayerChoice, setRelayerChoice] = useState<RelayerChoice>(SELF_RELAY);
  const [relayerOptions, setRelayerOptions] = useState<RelayerEndpoint[]>([]);
  const [relayerLoadError, setRelayerLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await discoverRelayers();
        if (cancelled) return;
        setRelayerOptions(list);
        // Auto-select the first network relayer as the default once the
        // discovery resolves. Without this, the dropdown shows the
        // network option but the underlying state stays on SELF_RELAY,
        // which surprises users who expect "use the relayer" to be the
        // default privacy-friendly choice.
        if (list.length > 0) setRelayerChoice(list[0]);
      } catch (e) {
        if (cancelled) return;
        setRelayerLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (stage === "done" || stage === "send") return;
    if (note && pool?.address && recipientValid) {
      setStage("ready");
    } else {
      setStage("input");
    }
  }, [note, pool?.address, recipientValid, stage]);

  async function handleSubmit() {
    if (!note || !pool || !pool.address || !recipientValid || !connected) return;
    setError(null);
    setStage("fetching-tree");
    try {
      const recipientAddr = Address.parse(recipient.trim());

      // Pre-flight: derive this note's nullifierHash client-side and
      // ask the relayer "is this nullifier already spent on-chain?"
      // BEFORE generating the proof. We hit the AUTHORITATIVE on-chain
      // get-method (/pool/is-spent → pool.isSpent), NOT the indexer's
      // /note/check, because the indexer can be ~5s behind the chain
      // and a stale "not spent" answer is a worse UX than a 1-2s
      // get-method round trip.
      //
      // Privacy: we send ONLY the nullifierHash, not the commitment,
      // so the relayer learns the candidate withdraw target without
      // linking it to a specific deposit. Same trade-off as the
      // standard /tree/path lookup.
      const { parseNote } = await import("@tonado/core");
      const parsedNote = await parseNote(raw.trim());
      if (await poolIsSpent(pool.address, parsedNote.deposit.nullifierHash)) {
        throw new Error("ERR_ALREADY_SPENT: this note has already been withdrawn");
      }

      // Pull the merkle tree leaves from the relayer (server-side
      // toncenter). The dApp never holds a toncenter API key, so this
      // is how every chain read flows.
      const preloadedCommitments = await loadCommitmentsFromRelayer(
        RELAYER_URL.replace(/\/+$/, ""),
        pool.address
      );

      if (relayerChoice === SELF_RELAY) {
        // Self-relay: the connected wallet IS the relayer. The proof
        // binds to it; the wallet pays gas and signs via TON Connect.
        // Fee is zero — there's no third party to pay.
        const relayerAddr = Address.parse(sender);
        setStage("proving");
        const args = await buildWithdrawArgs({
          note: raw.trim(),
          recipient: recipientAddr,
          relayer: relayerAddr,
          network: note.network,
          fee: 0n,
          preloadedCommitments,
        });
        const body = await buildWithdrawMessageBody(args);
        const bodyBase64 = body.toBoc().toString("base64");
        setStage("send");
        const res = await tonConnectUI.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 360,
          messages: [
            {
              address: pool.address,
              amount: WITHDRAW_ATTACHED.toString(),
              payload: bodyBase64,
            },
          ],
        });
        setTxHash(res.boc);
        setStage("done");
        return;
      }

      // Network-relayer path:
      //   1. Ask the relayer for its current fee parameters.
      //   2. Resolve its on-chain TON address (the SNARK binds to this).
      //   3. Compute the fee from denomination + feeBps.
      //   4. Build the proof with that relayer + that fee.
      //   5. POST the broadcast payload to its /withdraw.
      // No TON Connect involvement — the relayer's own wallet pays gas
      // and submits the on-chain tx.
      const status = await getStatus(relayerChoice.metadata.endpoint);
      // Resolution order for the relayer's TON address (the proof binds
      // to it):
      //   1. metadata.tonAddress (advertised by the relayer)
      //   2. accountHash from the on-chain bond record (registry mode)
      //   3. /status.relayerAddress — used when neither of the above is
      //      available (single-relayer fallback before the on-chain
      //      registry is configured). Trust here is "trust the URL we
      //      put in env" — same as any client/server config string.
      const relayerAddr =
        resolveRelayerAddress(relayerChoice) ??
        (status.relayerAddress ? Address.parse(status.relayerAddress) : null);
      if (!relayerAddr) {
        throw new Error(
          "Selected relayer has no resolvable TON address (no tonAddress in metadata, no on-chain bond, and /status returned no relayerAddress)."
        );
      }
      // Defense-in-depth: cross-check that the relayer's advertised
      // address matches whatever the registry recorded as bonded.
      if (relayerChoice.registered) {
        const advertisedHash = relayerAddr.hash.toString("hex");
        const bondedHash = relayerChoice.registered.accountHash.toString(16).padStart(64, "0");
        if (advertisedHash !== bondedHash) {
          throw new Error(
            "Relayer's advertised tonAddress doesn't match its on-chain bond. Refusing to send."
          );
        }
      }
      const fee = computeRelayerFee(
        BigInt(pool.denomination),
        status.feeBps,
        BigInt(status.feeMinNanoTon)
      );
      setStage("proving");
      const args = await buildWithdrawArgs({
        note: raw.trim(),
        recipient: recipientAddr,
        relayer: relayerAddr,
        network: note.network,
        fee,
        preloadedCommitments,
      });
      const payload = buildBroadcastPayload({
        pool: pool.address,
        args,
      });
      setStage("send");
      const res = await broadcastWithdraw(payload, relayerChoice.metadata.endpoint);
      setTxHash(`relayer seqno ${res.seqno}, fee ${res.fee}`);
      setStage("done");
    } catch (e) {
      setError(friendlyError(e));
      setStage("ready");
    }
  }

  return (
    <div className="flow flow--withdraw">
      <Steps
        current={stageIdxOf(stage)}
        steps={["Paste note", "Set recipient", "Prove", "Broadcast"]}
      />

      <label className="field">
        <span className="field__label">Deposit note</span>
        <textarea
          rows={3}
          spellCheck={false}
          placeholder="tonado-ton-10-mainnet-0x…"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        <span className="field__hint">
          {parseErr ? (
            <span className="field__error">{parseErr}</span>
          ) : note ? (
            <span className="field__ok">
              ✓ Recognised {note.amount} {assetIdLabel(note.asset)} note on {note.network}
            </span>
          ) : (
            "Paste the deposit note from your records."
          )}
        </span>
      </label>

      {savedNotes.length > 0 && !raw.trim() && (
        <div className="saved-notes-picker">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setShowSaved((s) => !s)}
          >
            {showSaved ? "Hide" : `Use one of ${savedNotes.length} saved notes…`}
          </button>
          {showSaved && (
            <ul className="saved-notes-picker__list">
              {savedNotes.map((s) => {
                const meta = parseNoteMetadata(s.raw);
                if (!meta) return null;
                return (
                  <li key={s.raw}>
                    <button
                      type="button"
                      className="saved-notes-picker__item"
                      onClick={() => {
                        setRaw(s.raw);
                        setShowSaved(false);
                      }}
                    >
                      <span className="saved-notes-picker__chip">
                        {meta.amount} {assetIdLabel(meta.asset)} · {meta.network}
                      </span>
                      <span className="saved-notes-picker__hex mono">…{s.raw.slice(-12)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {note && !pool && (
        <p className="warn">
          The pool{" "}
          <code>
            {note.asset}-{note.amount}
          </code>{" "}
          isn't deployed on the current network. Switch networks or check the note.
        </p>
      )}

      {pool && (
        <label className="field">
          <span className="field__label">Recipient address</span>
          <input
            type="text"
            spellCheck={false}
            autoComplete="off"
            placeholder="EQ… or UQ…"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
          <span className="field__hint">
            {recipient && !recipientValid ? (
              <span className="field__error">Doesn't look like a TON address.</span>
            ) : (
              "Funds land here. For maximum privacy use a fresh wallet."
            )}
          </span>
        </label>
      )}

      {pool && (
        <label className="field">
          <span className="field__label">Relayer</span>
          <select
            value={relayerChoice === SELF_RELAY ? SELF_RELAY : relayerChoice.metadata.endpoint}
            onChange={(e) => {
              const v = e.target.value;
              if (v === SELF_RELAY) {
                setRelayerChoice(SELF_RELAY);
                return;
              }
              const picked = relayerOptions.find((o) => o.metadata.endpoint === v);
              if (picked) setRelayerChoice(picked);
            }}
          >
            {/*
              Render every relayer the discovery returned, registered
              or not. The single-relayer-fallback entry (registered: null,
              endpoint = configured RELAYER_URL) is a legitimate first-
              class option until the on-chain registry is configured —
              filtering it out would leave only self-relay, which is the
              opposite of the privacy-preferring default we want.
            */}
            {relayerOptions.map((o) => (
              <option key={o.metadata.endpoint} value={o.metadata.endpoint}>
                {o.metadata.name ?? endpointHostname(o.metadata.endpoint)}
                {o.metadata.feeBps !== undefined
                  ? ` — ${(o.metadata.feeBps / 100).toFixed(2)}% fee`
                  : ""}
                {o.metadata.region ? ` · ${o.metadata.region}` : ""}
              </option>
            ))}
            <option value={SELF_RELAY}>Self-relay (your wallet pays gas, no relayer fee)</option>
          </select>
          <span className="field__hint">
            {relayerLoadError ? (
              <span className="field__error">
                Couldn't read relayer registry: {relayerLoadError}. Using self-relay.
              </span>
            ) : relayerChoice === SELF_RELAY ? (
              "Your connected wallet will sign and pay gas. Best for privacy if you're already comfortable using the same wallet for deposit and withdrawal."
            ) : (
              `${endpointHostname(relayerChoice.metadata.endpoint)} will pay gas and collect a fee from the withdrawal amount. The recipient receives denomination − fee.`
            )}
          </span>
        </label>
      )}

      {pool && (
        <div className="cta-row">
          <button
            type="button"
            className="btn btn--primary btn--lg"
            disabled={
              !note ||
              !recipientValid ||
              !connected ||
              stage === "fetching-tree" ||
              stage === "proving" ||
              stage === "send"
            }
            onClick={handleSubmit}
          >
            {!connected
              ? "Connect wallet to withdraw"
              : stage === "fetching-tree"
                ? "Reading pool history…"
                : stage === "proving"
                  ? "Generating ZK proof (5–15s)…"
                  : stage === "send"
                    ? "Confirm in your wallet…"
                    : "Withdraw"}
          </button>
        </div>
      )}

      {(stage === "fetching-tree" || stage === "proving" || stage === "send") && (
        <div className="pending pending--multi">
          <span className="pending__dot" />
          <div>
            <span>
              {stage === "fetching-tree"
                ? "Reading pool history…"
                : stage === "proving"
                  ? "Generating zero-knowledge proof (5–15s)…"
                  : "Submitting via your wallet…"}
            </span>
            <span className="pending__sub">
              All proof generation runs locally. Your note never leaves this browser.
            </span>
          </div>
        </div>
      )}

      {stage === "done" && (
        <div className="success">
          <p className="success__kicker">✓ Withdrawal submitted</p>
          <h3>Funds en route to your recipient.</h3>
          <p>
            The on-chain verifier accepted your proof; the pool marked your nullifier spent. No
            on-chain link remains between your deposit and this withdrawal address.
          </p>
          {txHash && <p className="success__hash mono mono--break">tx: {txHash.slice(0, 80)}…</p>}
        </div>
      )}

      {error && (
        <div className="error">
          <p className="error__friendly">{error.friendly}</p>
          <p className="error__technical">
            <span className="error__technical-label">Technical detail:</span>{" "}
            <code>{error.technical}</code>
          </p>
        </div>
      )}
    </div>
  );
}

function stageIdxOf(s: Stage): number {
  switch (s) {
    case "input":
      return 0;
    case "ready":
      return 1;
    case "fetching-tree":
      return 2;
    case "proving":
      return 2;
    case "send":
      return 3;
    case "done":
      return 3;
  }
}

function isPlausibleTonAddress(s: string): boolean {
  // Friendly form is 48 chars total (2-char prefix + 46-char base64url).
  // Accept either EQ/UQ (mainnet) or kQ/0Q (testnet) prefix.
  return /^(EQ|UQ|0Q|kQ)[A-Za-z0-9_-]{46}$/.test(s.trim());
}

// Silence unused-import lint for `isValidNote` — kept for future use if
// we add a "validate" button or other explicit-action UX.
void isValidNote;
